import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import { t } from 'i18next'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import { selectItalyFiscalCodesManagement } from './italyFiscalCodesManagementSelectors'
import {
  closeDeleteModal,
  closeModal,
  deleteRequest,
  loadFailure,
  loadRequest,
  loadSuccess,
  saveFailure,
  saveRequest,
} from './italyFiscalCodesManagementSlice'
import { toLocationPayload } from './toLocationPayload'
import {
  FiscalCodeField,
  FiscalCodeLocationsApiError,
  FiscalCodeLocationsListResponse,
  FiscalCodeTable,
  ItalyFiscalCodeLocationsRoutes,
  ItalyFiscalCodesAuthData,
  ItalyFiscalCodesManagementState,
} from './types'

export const italyFiscalCodesManagementActions = {
  LOAD: 'ITALY_FISCAL_CODES/LOAD',
  SAVE: 'ITALY_FISCAL_CODES/SAVE',
  DELETE: 'ITALY_FISCAL_CODES/DELETE',
}

const NAMESPACE = 'italyFiscalCodesManagement'
const HTTP_FORBIDDEN = 403
const INVALID = 'ITALY_FISCAL_CODE_LOCATION_INVALID'
const NOT_FOUND = 'ITALY_FISCAL_CODE_LOCATION_NOT_FOUND'
const ERROR_KEYS: Record<string, string> = {
  ITALY_FISCAL_CODE_LOCATION_ALREADY_EXISTS: 'already_exists',
  [NOT_FOUND]: 'not_found',
  ITALY_FISCAL_CODE_LOCATION_NOTHING_TO_UPDATE: 'nothing_to_update',
}

interface ApiRequest {
  endpoint?: string
  method?: string
  payload?: unknown
  queryParams?: Record<string, string>
  showErrorSnackbar?: boolean
}

const requestApi = callApi as unknown as (request: ApiRequest) => Generator

interface EnqueueAction {
  type: string
  enqueue: unknown
}

function* fiscalCodeRoutes(): Generator<unknown, ItalyFiscalCodeLocationsRoutes, ItalyFiscalCodesAuthData> {
  const authData = yield select(selectAuthData)

  return authData?.extraRoutes?.italyFiscalCodeLocations ?? {}
}

function* pageState(): Generator<unknown, ItalyFiscalCodesManagementState, ItalyFiscalCodesManagementState> {
  return yield select(selectItalyFiscalCodesManagement)
}

const tableEndpoint = (route: string | undefined, table: FiscalCodeTable): string | undefined =>
  route?.replace('%table%', table)

const rowEndpoint = (route: string | undefined, table: FiscalCodeTable, id: number): string | undefined =>
  tableEndpoint(route, table)?.replace('%id%', String(id))

const isApiError = (error: unknown): error is FiscalCodeLocationsApiError => error instanceof Error

const errorCodeOf = (error: unknown): string | undefined =>
  isApiError(error) ? error.responseData?.error?.code : undefined

const errorFieldOf = (error: unknown): FiscalCodeField | undefined => {
  const field = isApiError(error) ? error.responseData?.error?.details?.field : undefined

  return field === 'code' || field === 'name' ? field : undefined
}

const errorMessageOf = (error: unknown, table: FiscalCodeTable): string => {
  if (!isApiError(error)) return t(`${NAMESPACE}:errors.generic`)

  const code = errorCodeOf(error)
  const field = errorFieldOf(error)
  if (code === INVALID && field === 'code') return t(`${NAMESPACE}:errors.invalid_code.${table}`)
  if (code === INVALID && field === 'name') return t(`${NAMESPACE}:errors.invalid_name`)
  if (code !== undefined && ERROR_KEYS[code]) return t(`${NAMESPACE}:errors.${ERROR_KEYS[code]}`)
  if (error.status === HTTP_FORBIDDEN) return t(`${NAMESPACE}:errors.forbidden`)

  return error.message || t(`${NAMESPACE}:errors.generic`)
}

function* notify(enqueue: unknown, type: string, message: string): Generator {
  yield call(showSnackbar, enqueue, { type, message })
}

export function* loadLocationsSaga(): Generator {
  const { table, search, page } = (yield* pageState()) as ItalyFiscalCodesManagementState
  try {
    yield put(loadRequest())
    const routes = (yield* fiscalCodeRoutes()) as ItalyFiscalCodeLocationsRoutes
    const queryParams: Record<string, string> = { page: String(page) }
    const trimmedSearch = search.trim()
    if (trimmedSearch) queryParams.search = trimmedSearch

    const response = (yield* requestApi({
      endpoint: tableEndpoint(routes.list, table),
      queryParams,
      showErrorSnackbar: false,
    })) as FiscalCodeLocationsListResponse

    yield put(
      loadSuccess({
        locations: Array.isArray(response?.data) ? response.data : [],
        total: response?.meta?.total ?? 0,
        lastPage: response?.meta?.last_page ?? 1,
      }),
    )
  } catch (error: unknown) {
    yield put(loadFailure(t(`${NAMESPACE}:errors.load`, { reason: errorMessageOf(error, table) })))
  }
}

export function* saveLocationSaga({ enqueue }: EnqueueAction): Generator {
  const { table, draft } = (yield* pageState()) as ItalyFiscalCodesManagementState
  const payload = toLocationPayload(draft)
  const isEdit = draft.id !== null
  try {
    yield put(saveRequest())
    const routes = (yield* fiscalCodeRoutes()) as ItalyFiscalCodeLocationsRoutes
    yield* requestApi({
      endpoint: draft.id === null ? tableEndpoint(routes.create, table) : rowEndpoint(routes.update, table, draft.id),
      method: isEdit ? 'PUT' : 'POST',
      payload,
      showErrorSnackbar: false,
    })
    yield put(closeModal())
    const successKey = isEdit ? 'updated' : 'created'
    yield* notify(enqueue, SNACKBAR_TYPES.SUCCESS, t(`${NAMESPACE}:success.${successKey}`, { code: payload.code }))
    yield* loadLocationsSaga()
  } catch (error: unknown) {
    if (errorCodeOf(error) === NOT_FOUND) {
      yield put(closeModal())
      yield* notify(enqueue, SNACKBAR_TYPES.WARNING, errorMessageOf(error, table))
      yield* loadLocationsSaga()
      return
    }
    yield put(saveFailure({ field: errorFieldOf(error), message: errorMessageOf(error, table) }))
  }
}

export function* deleteLocationSaga({ enqueue }: EnqueueAction): Generator {
  const { table, deleteTarget } = (yield* pageState()) as ItalyFiscalCodesManagementState
  if (deleteTarget === null) return

  try {
    yield put(deleteRequest())
    const routes = (yield* fiscalCodeRoutes()) as ItalyFiscalCodeLocationsRoutes
    yield* requestApi({
      endpoint: rowEndpoint(routes.delete, table, deleteTarget.id),
      method: 'DELETE',
      showErrorSnackbar: false,
    })
  } catch (error: unknown) {
    const alreadyRemoved = errorCodeOf(error) === NOT_FOUND
    yield put(closeDeleteModal())
    yield* notify(enqueue, alreadyRemoved ? SNACKBAR_TYPES.WARNING : SNACKBAR_TYPES.ERROR, errorMessageOf(error, table))
    if (alreadyRemoved) yield* loadLocationsSaga()
    return
  }

  yield put(closeDeleteModal())
  yield* notify(enqueue, SNACKBAR_TYPES.SUCCESS, t(`${NAMESPACE}:success.deleted`, { code: deleteTarget.code }))
  yield* loadLocationsSaga()
}

export default function* italyFiscalCodesManagementSagas() {
  yield takeLatest(italyFiscalCodesManagementActions.LOAD, loadLocationsSaga)
  yield takeLeading(italyFiscalCodesManagementActions.SAVE, saveLocationSaga)
  yield takeLeading(italyFiscalCodesManagementActions.DELETE, deleteLocationSaga)
}
